import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link } from "react-router-dom";
import { Sparkles, Loader2, AlertTriangle, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import api, { formatError } from "../lib/api";
import { MD_COMPONENTS, REMARK_PLUGINS } from "../lib/markdown";

export const SignalAiModal = ({ signal, open, onClose, locked = false }) => {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !signal || locked) return;
    let active = true;
    setLoading(true);
    setError("");
    setAnalysis("");
    api
      .post(`/signals/${signal.id}/ai`)
      .then((r) => { if (active) setAnalysis(r.data.analysis || ""); })
      .catch((e) => { if (active) setError(formatError(e?.response?.data?.detail)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, signal?.id, locked]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="signal-ai-modal"
        className="max-w-2xl max-h-[85vh] overflow-y-auto bg-[#0f0f0f] border-white/10 text-zinc-100"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-400">
            <Sparkles size={18} /> Raeinexchange AI
          </DialogTitle>
        </DialogHeader>

        {signal && (
          <div className="text-sm text-zinc-400 -mt-1 mb-2">
            <span className="text-zinc-200 font-medium">{signal.symbol}</span> · {signal.direction}
            {signal.timeframe ? ` · ${signal.timeframe}` : ""}
            {signal.strategy_label ? ` · ${signal.strategy_label}` : ""}
          </div>
        )}

        {locked && (
          <div className="flex flex-col items-center text-center gap-3 py-8" data-testid="signal-ai-locked">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <Lock className="text-emerald-400" size={22} />
            </div>
            <p className="text-sm text-zinc-300">L'analisi <span className="text-emerald-300 font-medium">Raeinexchange AI</span> è riservata agli abbonati VIP.</p>
            <p className="text-xs text-zinc-500">È gratuita solo per i segnali XAUUSD in M15 e H1.</p>
            <Link to="/pricing" data-testid="signal-ai-upgrade" className="mt-1 text-xs font-medium px-4 py-2 rounded-full bg-emerald-500 text-black hover:bg-emerald-400 transition-colors">
              Passa a VIP
            </Link>
          </div>
        )}

        {!locked && loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-zinc-400" data-testid="signal-ai-loading">
            <Loader2 className="animate-spin text-emerald-400" size={28} />
            <p className="text-sm">Analisi del segnale con le news aggiornate…</p>
          </div>
        )}

        {!loading && error && (
          <div className="flex items-center gap-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-3 text-sm" data-testid="signal-ai-error">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {!loading && !error && analysis && (
          <div data-testid="signal-ai-analysis" className="text-sm leading-relaxed">
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={MD_COMPONENTS}>{analysis}</ReactMarkdown>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
